import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card } from 'reactstrap';
import Loader from '../common/Loader.jsx';
import ResponseErrorMessage from '../common/ResponseErrorMessage.jsx';
import { fetchTp3, standardHandle } from './standardsApi.js';

/**
 * Loads the children of one node (roots when parentUuid is null) once, on mount
 *
 * @param frameworkUuid
 * @param parentUuid
 * @param onForbidden
 * @returns {{items: Array|null, loading: boolean, error: object|null}}
 */
const useChildren = (frameworkUuid, parentUuid, onForbidden) => {
	const [items, setItems] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		const abortController = new AbortController();

		setLoading(true);
		fetchTp3('GetTestPrepStandards', { frameworkUuid, parentUuid }, abortController.signal)
			.then(payload => setItems(payload.items))
			.catch(e => {
				if (abortController.signal.aborted) {
					return;
				}
				if (e.status === 403) {
					onForbidden();
				} else {
					setError(e);
				}
			})
			.finally(() => !abortController.signal.aborted && setLoading(false));

		return () => abortController.abort();
	}, [frameworkUuid, parentUuid]);

	return { items, loading, error };
};

/**
 * One standard in the browse tree: a selectable leaf, or a container that opens to its children
 */
const StandardNode = props => {
	const { node, frameworkUuid, selectedUuid, onSelect, onForbidden } = props;
	const [open, setOpen] = useState(false);
	const handle = standardHandle(node);
	const coverage = node.alignedLeafCount + ' of ' + node.leafCount + ' with content';
	const muted = node.alignedLeafCount === 0 ? ' text-muted' : '';

	if (node.isLeaf) {
		return (
			<li>
				<button type={'button'}
				        className={'btn btn-link standard-leaf' + muted +
					        (selectedUuid === node.uuid ? ' active' : '')}
				        aria-pressed={selectedUuid === node.uuid}
				        onClick={() => onSelect(node.uuid)}>
					{ handle !== '' && <strong>{ handle } </strong> }{ node.statement }
				</button>
			</li>
		);
	}

	return (
		<li>
			<Card className={'standard-group'}>
				<Button color={'light'}
				        className={(open ? 'toggle-on ' : '') + 'collapse-button standard-group-toggle' + muted}
				        aria-expanded={open}
				        onClick={() => setOpen(!open)}>
					<span>{ handle !== '' && <strong>{ handle } </strong> }{ node.statement }</span>
					<small className={'standard-coverage'}>{ coverage }</small>
					<i className={'far fa-chevron-down'} aria-hidden="true"></i>
				</Button>
				{
					open &&
						// eslint-disable-next-line no-use-before-define -- StandardNode and StandardList recurse
						<StandardList frameworkUuid={frameworkUuid} parentUuid={node.uuid}
						              selectedUuid={selectedUuid} onSelect={onSelect}
						              onForbidden={onForbidden} />
				}
			</Card>
		</li>
	);
};

/**
 * The children of one node as a list; used recursively
 */
const StandardList = props => {
	const { frameworkUuid, parentUuid, selectedUuid, onSelect, onForbidden } = props;
	const { items, loading, error } = useChildren(frameworkUuid, parentUuid, onForbidden);

	if (loading) {
		return <Loader />;
	}
	if (error !== null) {
		return <ResponseErrorMessage error={error} />;
	}

	return (
		<ul className={'standard-list'}>
			{
				items.map(node =>
					<StandardNode key={node.uuid} node={node} frameworkUuid={frameworkUuid}
					              selectedUuid={selectedUuid} onSelect={onSelect} onForbidden={onForbidden} />
				)
			}
		</ul>
	);
};

/**
 * Course → strand → standard browse accordion for one framework
 */
const StandardBrowser = props => {
	const { frameworkUuid, selectedUuid, onSelect, onForbidden } = props;

	return (
		<nav aria-label={'Browse standards'} className={'standard-browser'}>
			<StandardList key={frameworkUuid} frameworkUuid={frameworkUuid} parentUuid={null}
			              selectedUuid={selectedUuid} onSelect={onSelect} onForbidden={onForbidden} />
		</nav>
	);
};

const nodeProps = {
	frameworkUuid: PropTypes.string.isRequired,
	selectedUuid: PropTypes.string,
	onSelect: PropTypes.func.isRequired,
	onForbidden: PropTypes.func.isRequired
};

StandardNode.propTypes = { ...nodeProps, node: PropTypes.object.isRequired };
StandardList.propTypes = { ...nodeProps, parentUuid: PropTypes.string };
StandardBrowser.propTypes = nodeProps;

export default StandardBrowser;
